import axios from 'axios';

import { datadogRum } from 'root/datadog/datadogRum';

export const LAYAWAY_SO_SUCCESS_REDIRECT = '/register.php?form_name=change_view&view=sale';

export const getTextByTransactionType = (transactionType: TransactionType) => {
  switch (transactionType) {
    case 'layaway':
      return {
        title: 'Layaway items in this sale are set to be removed from a different inventory',
        recommendedText: 'Layaway items will be deducted from your current location’s inventory.',
        nonRecommendedText: 'Items in this layaway will be removed from a different inventory.',
      };
    case 'special_order':
      return {
        title: 'Special order items in this sale are set to be removed from a different inventory',
        recommendedText:
          'Special order items will be deducted from your current location’s inventory.',
        nonRecommendedText:
          'Items in this special order will be removed from a different inventory.',
      };
    case 'regular_sale':
    case 'quote':
      return {
        title: 'Items in this sale are set to be removed from a different location’s inventory',
        recommendedText: 'All items will be deducted from your current location’s inventory.',
        nonRecommendedText:
          'Items in this sale will be removed from a different inventory and new items will be removed from this location’s inventory.',
      };
    default:
      return { title: '', recommendedText: '', nonRecommendedText: '' };
  }
};

export const getPutPayload = (saleLinesToUpdate: SaleLineFromApi[], sessionShopID: string) => {
  if (saleLinesToUpdate.length > 1)
    return {
      SaleLines: {
        SaleLine: saleLinesToUpdate.map(({ saleLineID }) => ({
          saleLineID,
          shopID: sessionShopID,
        })),
      },
    };

  const [firstSaleLine] = saleLinesToUpdate;

  return {
    SaleLines: {
      SaleLine: {
        saleLineID: firstSaleLine.saleLineID,
        shopID: sessionShopID,
      },
    },
  };
};

export const getResource = async ({
  requestFunction,
  isUsingFetchApi = false,
}: {
  requestFunction: () => Promise<any>;
  isUsingFetchApi?: boolean;
}): Promise<[data: any, error: Error | null]> => {
  try {
    // When using axios
    if (!isUsingFetchApi) {
      const response = await requestFunction();
      return [response.data, null];
    }

    // When fetch API is used
    const response = await requestFunction();
    if (!response.ok) {
      throw new Error(response.statusText || 'Something went wrong');
    }
    const data = await response.json();
    return [data, null];
  } catch (e) {
    // eslint-disable-next-line no-console
    console.info('Request failed', e);
    return [null, e];
  }
};

type CommonMultiLocationSubmitHandlerProps = {
  saleLinesToUpdate: SaleLineFromApi[];
  accountId: string;
  sessionShopID: string;
  closeModal: () => void;
  onHttpCallError: () => void;
};

type RegularSaleOrQuoteHandlerProps = CommonMultiLocationSubmitHandlerProps & {
  saleId: string | undefined;
  saleType: 'regular_sale' | 'quote';
  shouldMoveSaleLines: ShouldMoveSaleLine;
};

type LayawayOrSpecialOrderHandlerProp = CommonMultiLocationSubmitHandlerProps & {
  saleType: 'layaway' | 'special_order';
  shouldMoveSaleLines: ShouldMoveSaleLine;
  replaceSaleLine: boolean;
};

/*
  For regular_sale, quote

  Load the sale by going to

  /register.php?form_name=load_transaction&transaction_id=3615 OR
  /register.php?form_name=load_quote&transaction_id=3651

  if success
  PUT to update saleLines
*/
export const regularSaleOrQuoteHandler = async ({
  shouldMoveSaleLines,
  saleLinesToUpdate,
  sessionShopID,
  closeModal,
  accountId,
  saleId,
  saleType,
  onHttpCallError,
}: RegularSaleOrQuoteHandlerProps) => {
  const putRequestUrl = `/API/Account/${accountId}/Sale/${saleId}.json`;
  let loadTransactionUrl: string;
  if (saleType === 'regular_sale') {
    loadTransactionUrl = `/register.php?form_name=load_transaction&transaction_id=${saleId}`;
  } else {
    loadTransactionUrl = `/register.php?form_name=load_quote&transaction_id=${saleId}`;
  }

  if (isRecommendedOptionSelected({ saleLinesToUpdate, sessionShopID, shouldMoveSaleLines })) {
    const [loadResponseData, _error] = await getResource({
      requestFunction: () => axios.get(`${loadTransactionUrl}`),
    });

    if (!isTransactionLoadedSuccessfully(loadResponseData)) {
      onHttpCallError();
    }

    if (isTransactionLoadedSuccessfully(loadResponseData)) {
      const [putResponseData, regularSaleMoveError] = await getResource({
        requestFunction: () =>
          axios.put(putRequestUrl, getPutPayload(saleLinesToUpdate, sessionShopID)),
      });

      if (regularSaleMoveError) {
        onHttpCallError();
      }

      if (putResponseData) {
        closeModal();
        window.merchantos.utils.openUrl('/register.php');
      }
    }
  } else {
    closeModal();
    window.merchantos.utils.openUrl(loadTransactionUrl);
  }
};

/*
  For layaways
  Send GET to /register.php?form_name=complete_layaway_line&transaction_line_id=78
  If success parse html and get saleId and transaction status and transaction_id to load

  if transaction_pass
  PUT to update saleLines
  if success, open /register.php


  For Special Orders
  Send GET to /register.php?form_name=complete_special_order_line&transaction_line_id=29
  If success parse html and get saleId and transaction status and transaction_id to load

  if transaction_pass
  PUT to update saleLines
  if success, open /register.php
*/
export const layawayOrSpecialOrderHandler = async ({
  closeModal,
  onHttpCallError,
  saleLinesToUpdate,
  accountId,
  sessionShopID,
  shouldMoveSaleLines,
  saleType,
  replaceSaleLine,
}: LayawayOrSpecialOrderHandlerProp) => {
  let completeAllUrl: string;
  let loadSaleLineIdUrl: string;

  if (saleType === 'layaway') {
    completeAllUrl = `/register.php?form_name=complete_all&type=layaway`;
    loadSaleLineIdUrl = `/register.php?form_name=complete_layaway_line&transaction_line_id=`;
  } else {
    completeAllUrl = `/register.php?form_name=complete_all&type=special_order`;
    loadSaleLineIdUrl = `/register.php?form_name=complete_special_order_line&transaction_line_id=`;
  }

  if (isRecommendedOptionSelected({ saleLinesToUpdate, sessionShopID, shouldMoveSaleLines })) {
    const isASingleLineUpdate = saleLinesToUpdate.length === 1;
    if (isASingleLineUpdate) {
      await updateSingleSaleLine({
        accountId,
        closeModal,
        loadSaleLineIdUrl,
        onHttpCallError,
        saleLinesToUpdate,
        sessionShopID,
        replaceSaleLine,
      });
    } else {
      await updateMultipleSaleLines({
        accountId,
        closeModal,
        completeAllUrl,
        onHttpCallError,
        saleLinesToUpdate,
        sessionShopID,
      });
    }
  } else {
    handleNonRecommendedLayawaysSpecialOrderSelection({
      closeModal,
      completeAllUrl,
      loadSaleLineIdUrl,
      saleLinesToUpdate,
    });
  }
};

export const processMultiLocationSaleVerification = async (response: Response) => {
  const currentShopID = window.merchantos.session.getShop()?.shopID;

  try {
    const {
      Sale: { quoteID, shopID, saleID },
    } = await response.json();

    if (window.openMultiLocationSaleModal && quoteID !== '0' && shopID !== currentShopID) {
      window.openMultiLocationSaleModal({
        saleType: 'quote',
        saleId: saleID,
      });
      return true;
    }
  } catch (err) {
    datadogRum.addError(err);
  }
  return false;
};

const handleNonRecommendedLayawaysSpecialOrderSelection = ({
  closeModal,
  completeAllUrl,
  saleLinesToUpdate,
  loadSaleLineIdUrl,
}: Pick<CommonMultiLocationSubmitHandlerProps, 'closeModal' | 'saleLinesToUpdate'> & {
  completeAllUrl: string;
  loadSaleLineIdUrl: string;
}) => {
  closeModal();
  if (saleLinesToUpdate?.length === 1) {
    const [{ saleLineID }] = saleLinesToUpdate;
    window.merchantos.utils.openUrl(`${loadSaleLineIdUrl}${saleLineID}`);
  }
  if (saleLinesToUpdate?.length > 1) {
    window.merchantos.utils.openUrl(completeAllUrl);
  }
};

const isRecommendedOptionSelected = ({
  saleLinesToUpdate,
  sessionShopID,
  shouldMoveSaleLines,
}: Pick<CommonMultiLocationSubmitHandlerProps, 'saleLinesToUpdate' | 'sessionShopID'> & {
  shouldMoveSaleLines: ShouldMoveSaleLine;
}) => !!shouldMoveSaleLines && !!saleLinesToUpdate.length && !!sessionShopID;

const updateSingleSaleLine = async ({
  closeModal,
  accountId,
  onHttpCallError,
  saleLinesToUpdate,
  sessionShopID,
  loadSaleLineIdUrl,
  replaceSaleLine,
}: CommonMultiLocationSubmitHandlerProps & {
  loadSaleLineIdUrl: string;
  replaceSaleLine: boolean;
}) => {
  const [{ saleLineID }] = saleLinesToUpdate;
  const [responseData, _error] = await getResource({
    requestFunction: () =>
      axios.get(`${loadSaleLineIdUrl}${saleLineID}${replaceSaleLine ? '&replace=1' : ''}`),
  });

  const responseHtml = new DOMParser().parseFromString(responseData, 'text/html');
  const saleId = responseHtml.querySelector('#register')?.getAttribute('transaction_id');

  if (!isTransactionLoadedSuccessfully(responseHtml)) {
    onHttpCallError();
  }

  if (saleId && isTransactionLoadedSuccessfully(responseHtml)) {
    const [putResponseData, putError] = await getResource({
      requestFunction: () =>
        axios.put(
          `/API/Account/${accountId}/Sale/${saleId}.json`,
          getPutPayload(saleLinesToUpdate, sessionShopID)
        ),
    });

    if (putResponseData) {
      closeModal();
      window.merchantos.utils.openUrl(LAYAWAY_SO_SUCCESS_REDIRECT);
    }

    if (putError) {
      onHttpCallError();
    }
  }
};

const updateMultipleSaleLines = async ({
  accountId,
  closeModal,
  completeAllUrl,
  onHttpCallError,
  saleLinesToUpdate,
  sessionShopID,
}: CommonMultiLocationSubmitHandlerProps & { completeAllUrl: string }) => {
  const [responseData, _getError] = await getResource({
    requestFunction: () => axios.get(completeAllUrl),
  });

  const responseHtml = new DOMParser().parseFromString(responseData, 'text/html');
  const saleId = responseHtml.querySelector('#register')?.getAttribute('transaction_id');

  if (!isTransactionLoadedSuccessfully(responseHtml)) {
    onHttpCallError();
  }

  if (saleId && isTransactionLoadedSuccessfully(responseHtml)) {
    const [putResponseData, putError] = await getResource({
      requestFunction: () =>
        axios.put(
          `/API/Account/${accountId}/Sale/${saleId}.json`,
          getPutPayload(saleLinesToUpdate, sessionShopID)
        ),
    });

    if (putResponseData) {
      closeModal();
      window.merchantos.utils.openUrl(LAYAWAY_SO_SUCCESS_REDIRECT);
    }

    if (putError) {
      onHttpCallError();
    }
  }
};

const isTransactionLoadedSuccessfully = (htmlResponse: string | Document): boolean => {
  let parsedHtml;
  if (typeof htmlResponse === 'string') {
    parsedHtml = new DOMParser().parseFromString(htmlResponse, 'text/html');
  } else {
    parsedHtml = htmlResponse;
  }
  return (
    parsedHtml.querySelector('#transaction_lines_loaded_status')?.getAttribute('value') === 'true'
  );
};
